
'use client';

import * as React from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import Link from 'next/link';
import { Loader2 } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { FirebaseUserProfile } from '@/lib/types';
import { isPaidLikeMembership } from '@/lib/membership';
import { trackUserActivity } from '@/lib/activity';
import { E } from '@/lib/editorial';

export function ClientProvider({ children }: { children: React.ReactNode }) {
  const MAX_SESSION_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [retryCount, setRetryCount] = React.useState(0);
  const router = useRouter();
  const pathname = usePathname();

  const initializeAuth = React.useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);
      
      // Set a timeout for the entire auth initialization
      const authTimeout = setTimeout(() => {
        console.warn('Auth initialization taking longer than expected');
      }, 10000); // 10 seconds warning
      
      const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
        try {
          clearTimeout(authTimeout);

          // Enforce max signed-in age (7 days) before any route logic.
          if (user) {
            const tokenResult = await user.getIdTokenResult();
            const authTimeMs = tokenResult.authTime ? new Date(tokenResult.authTime).getTime() : null;
            const sessionExpired = !authTimeMs || (Date.now() - authTimeMs) > MAX_SESSION_AGE_MS;

            if (sessionExpired) {
              await signOut(auth);
              router.push('/auth/login?reason=session_expired');
              setIsLoading(false);
              return;
            }

            trackUserActivity(user.uid);
          }
          
          // Define public pages that anyone can access, logged in or not.
          const publicPages = [
              '/', '/auth/login', '/auth/signup', '/auth/forgot-password', '/auth/action', '/auth/complete-profile',
              '/membership', '/about', '/contact', '/services', '/terms', '/privacy', '/unsubscribe'
          ];
          const isPublicPage = publicPages.some(p => pathname.startsWith(p));

          // If the page is public, anyone can see it. Stop loading.
          if (isPublicPage) {
            setIsLoading(false);
            return;
          }

          // If the page is NOT public, we check for a user.
          if (!user) {
            // If no user, redirect to login/membership.
            router.push('/membership');
            setIsLoading(false);
          } else {
            // A user is logged in. Check their profile to see if they should be redirected.
            try {
              const userDocRef = doc(db, 'users', user.uid);
              
              // Add timeout to Firestore query using Promise.race
              const fetchTimeout = new Promise<never>((_, reject) => 
                setTimeout(() => reject(new Error('Firestore query timeout')), 8000)
              );
              
              const userDocSnap = await Promise.race([
                getDoc(userDocRef),
                fetchTimeout
              ]);
              
              if (userDocSnap.exists()) {
                const profile = userDocSnap.data() as FirebaseUserProfile;
                const isPaid = isPaidLikeMembership(profile.membershipTier);
                const isAgentReferred = !!profile.agentId;
                const isAdmin = !!profile.isAdmin;

                // Redirect free users away from protected content to the membership page.
                if (!isPaid && !isAgentReferred && !isAdmin) {
                  router.push('/membership');
                } else {
                  // User is paid, referred, or admin, so they can access the protected page.
                  setIsLoading(false);
                }
              } else {
                // If profile doesn't exist yet, they're likely in the signup flow.
                // Allow them to access auth pages and complete-profile page.
                const authPages = ['/auth/login', '/auth/signup', '/auth/forgot-password', '/auth/action', '/auth/complete-profile'];
                const isAuthPage = authPages.some(p => pathname.startsWith(p));
                
                if (isAuthPage) {
                  // User is on an auth page, let them proceed
                  setIsLoading(false);
                } else {
                  // User is not on an auth page, redirect to complete profile
                  router.push('/auth/complete-profile');
                }
              }
            } catch (firestoreError) {
              console.error('Error fetching user profile from Firestore:', firestoreError);
              // On Firestore error, allow user through if authenticated
              // They can retry specific actions that need profile data
              setIsLoading(false);
              if ((firestoreError as Error).message?.includes('timeout')) {
                console.warn('Firestore query timed out, allowing authenticated user through');
              }
            }
          }
        } catch (error) {
          clearTimeout(authTimeout);
          console.error('Error in auth state change handler:', error);
          // Don't show error for temporary issues, just log and allow through
          setIsLoading(false);
        }
      });

      // Cleanup subscription on unmount
      return () => {
        clearTimeout(authTimeout);
        unsubscribe();
      };
    } catch (error) {
      console.error('Error initializing auth:', error);
      setError('Failed to initialize authentication. Please try refreshing the page.');
      setIsLoading(false);
    }
  }, [pathname, router]);

  React.useEffect(() => {
    const cleanup = initializeAuth();
    return () => {
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then(cleanupFn => cleanupFn && cleanupFn());
      }
    };
  }, [initializeAuth]);

  const handleRetry = React.useCallback(() => {
    setRetryCount((prev) => prev + 1);
    setError(null);
    setIsLoading(true);
    initializeAuth();
  }, [initializeAuth]);

  const btnRow: React.CSSProperties = {
    borderRadius: 0,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    cursor: 'pointer',
    fontFamily: E.sans,
    fontSize: 11,
    letterSpacing: '0.22em',
    textTransform: 'uppercase',
    padding: '16px 32px',
  };

  if (error) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: E.cream,
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px',
          boxSizing: 'border-box',
        }}
      >
        <div className="e-pad" style={{ width: '100%', maxWidth: 720 }}>
          <div style={{ ...E.label, marginBottom: 12 }}>Error · Connection</div>

          <p
            style={{
              fontFamily: E.serif,
              fontSize: 'clamp(56px, 12vw, 96px)',
              fontWeight: 400,
              fontStyle: 'italic',
              lineHeight: 0.95,
              margin: '0 0 8px',
              color: E.goldDeep,
              opacity: 0.42,
            }}
          >
            …
          </p>

          <h1 style={{ ...E.h1, fontSize: 'clamp(28px, 4vw, 40px)', marginBottom: 16 }}>Connection error.</h1>

          <p style={{ ...E.body, color: E.muted, maxWidth: 520, marginBottom: retryCount > 2 ? 20 : 28 }}>
            {error}
          </p>

          {retryCount > 2 && (
            <p
              style={{
                ...E.body,
                fontSize: 14,
                color: E.muted,
                maxWidth: 520,
                marginBottom: 28,
                padding: 16,
                background: E.cream2,
                border: `1px solid ${E.rule}`,
              }}
            >
              If this continues, wait a few minutes and try again — it may be a temporary service outage.
            </p>
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              paddingTop: 8,
              borderTop: `1px solid ${E.rule}`,
            }}
          >
            <button type="button" onClick={handleRetry} style={{ ...E.btn, ...btnRow }}>
              Try again{retryCount > 0 ? ` (${retryCount})` : ''}
            </button>
            <Link href="/" style={{ ...E.btnGhost, ...btnRow }}>
              Return home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    // Check if current page is public to show appropriate loading message
    const publicPages = [
      '/', '/auth/login', '/auth/signup', '/auth/forgot-password', '/auth/action', '/auth/complete-profile',
      '/membership', '/about', '/contact', '/services', '/terms', '/privacy', '/unsubscribe'
    ];
    const isPublicPage = publicPages.some(p => pathname.startsWith(p));
    
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: E.cream,
          zIndex: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
          <Loader2 style={{ width: 40, height: 40, color: E.gold, animation: 'spin 1s linear infinite' }} />
          {!isPublicPage && (
            <p style={{ ...E.body, color: E.muted, margin: 0 }}>Loading member area…</p>
          )}
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
